import React, { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, Drawer, List, Divider, Hidden, Link } from "@mui/material";
import { BrandLogo } from "@theatticusapp/atticus-shared-components";
import { FlowProgress } from "./FlowProgress";
import { FlowProgressSection } from "./FlowProgressSection";
import { useAuthContext } from "../../../AuthContextWrapper";
import { AppScreen } from "../../AppLayout/SideBar/navItems";
import NavItem from "../../shared/NavItem";

interface Props {
  drawerWidth: number;
  onMobileClose?: () => void;
  openMobile: boolean;
}

export const FlowSideBar = ({
  drawerWidth,
  onMobileClose,
  openMobile = false,
}: Props): JSX.Element => {
  const location = useLocation();
  const { flow, activeEstate, user } = useAuthContext()
  const { screenName } = useParams();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const currentSectionIndex = flow?.getSectionIndex(currentScreen) || 0;

  const getContent = (logo: boolean): JSX.Element => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      className="flow-sidebar-drawer-content"
    >
      {logo && (
        <Box
          sx={{
            p: 2,
          }}
        >
          <Link href="/app/home">
            <BrandLogo
              advisorGroupKey={
                activeEstate?.advisorGroup?.title || user?.advisorGroupName
              }
              secondary
              sx={{ maxWidth: "6.75rem" }}
            />
          </Link>
        </Box>
      )}
      <Box
        sx={{
          my: 1,
          mx: 2,
        }}
      >
        <FlowProgress
          step={flow?.getCurrentStep(currentScreen)}
          totalSteps={flow?.getTotalSteps()}
          completedAll={currentScreen?.completedAll || false}
        />
      </Box>
      <Box
        sx={{
          my: 1,
          mx: 2,
        }}
      >
        <List>
          {flow?.sections?.map((section: string, index: number) => (
            <React.Fragment key={section}>
              <FlowProgressSection
                title={section}
                completedAll={currentScreen?.completedAll || false}
                complete={index < currentSectionIndex}
                current={index === currentSectionIndex}
                last={index === flow?.sections?.length - 1}
              />
            </React.Fragment>
          ))}
        </List>
      </Box>
      <Box flexGrow={1} />
      <Divider variant="middle" key="FlowSideBar-divider2" />
      <Hidden mdUp>
        <List>
          {[
            {
              title: "Sign out",
              path: "/sign-out",
              icon: <></>,
            },
          ].map((screen: AppScreen) => (
            <React.Fragment key={screen.title}>
              <NavItem
                path={screen.path}
                title={screen.title}
                icon={screen.icon}
              />
            </React.Fragment>
          ))}
        </List>
      </Hidden>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant="temporary"
        open={openMobile}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
        className="flow-mobile-drawer"
      >
        {getContent(false)}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            boxShadow: "none",
          },
        }}
        className="flow-stationary-drawer"
        open
      >
        {getContent(true)}
      </Drawer>
    </Box>
  );
};

// const mapStateToProps = ({ user }: StoreState): { user: User } => {
//   return { user };
// };
//
// export const FlowSideBar = connect(mapStateToProps)(_NavBar);
